import { createRouter, createWebHistory } from 'vue-router'
import { useAuthUserStore } from "@/store/auth.js";
import { usePermissionStore } from "@/store/perms.js";
import { useRememberStore } from '@/store/remember';

const routes = [
    {
        path: '/',
        name: 'Login',
        component: () => import('@/components/login/Login.vue'),
        meta: {
            hideNavbar: true,
            permission_requirement: undefined,
            theme: 'general',
        },
    },
    {
        path: '/diagnostic',
        name: 'Diagnostic',
        component: () => import('@/components/_shared/Diagnostic.vue'),
        meta: {
            hideNavbar: true,
            permission_requirement: undefined,
            theme: 'general',
        },
    },
    {
        path: '/forbidden',
        name: '_403',
        component: () => import('@/components/_shared/_403.vue'),
        meta: {
            permission_requirement: undefined,
            theme: 'general',
        },
    },
    {
        path: '/undefined',
        name: '_404',
        component: () => import('@/components/_shared/_404.vue'),
        meta: {
            permission_requirement: undefined,
            theme: 'general',
        },
    },
    {
        path: '/home',
        name: 'Home',
        component: () => import('@/components/home/Home.vue'),
        meta: {
            permission_requirement: 'canSeeHome',
            theme: 'general',
        },
    },
    {
        path: '/clientChat',
        name: 'Client Chat',
        component: () => import('@/components/home/ClientChat.vue'),
        meta: {
            permission_requirement: 'canMakeCalls',
            theme: 'general',
        },
    },
    {
        path: '/calendar',
        name: 'Calendar',
        component: () => import('@/components/calendar/Calendar.vue'),
        meta: {
            permission_requirement: 'canSeeCalendar',
            theme: 'general',
        },
    },
    {
        path: '/chat/:id',
        name: 'Chat',
        component: () => import('@/components/conversation/Chat.vue'),
        meta: {
            permission_requirement: ['canAnswerCalls', 'canMakeCalls'],
            theme: 'general',
        },
    },
    {
        path: '/conversations',
        name: 'Conversations',
        component: () => import('@/components/conversation/ChatList.vue'),
        meta: {
            permission_requirement: 'canSeeCallsList',
            theme: 'general',
        },
    },
    {
        path: '/config',
        name: 'Configuration',
        component: () => import('@/components/config/Settings.vue'),
        meta: {
            permission_requirement: 'canSeeConfig',
            theme: 'general',
        },
    },
    {
        path: '/user/:id',
        name: 'User Form',
        component: () => import('@/components/config/UserForm.vue'),
        meta: {
            permission_requirement: 'canSeeConfig',
            theme: 'general',
        },
    },
    {
        path: '/factory/:id',
        name: 'Factory Form',
        component: () => import('@/components/config/FactoryForm.vue'),
        meta: {
            permission_requirement: 'canSeeConfig',
            theme: 'general',
        },
    },
    {
        path: '/connection',
        name: 'Connection Test',
        component: () => import('@/components/connection/ConnectionTest.vue'),
        meta: {
            permission_requirement: 'canSeeTestConnection',
            theme: 'general',
        },
    },
    {
        path: '/answerer',
        name: 'Answerer',
        component: () => import('@/components/answerer/Answerer.vue'),
        meta: {
            permission_requirement: 'canSeeAnswerer',
            theme: 'general',
        },
    },
    {
        path: '/connectionHistoric',
        name: 'Connection Historic',
        component: () => import('@/components/historic/Connection.vue'),
        meta: {
            permission_requirement: 'canSeeHistoTest',
            theme: 'general',
        },
    },
    {
        path: '/interventionReportHistoric/:factoryName?',
        name: 'Intervention Report Historic',
        component: () => import('@/components/historic/InterventionReport.vue'),
        meta: {
            permission_requirement: 'canSeeHistoRI',
            theme: 'general',
        },
    },
    {
        path: '/agreement',
        name: 'Agreement',
        component: () => import('@/components/agreement/Agreement.vue'),
        meta: {
            permission_requirement: 'canSeeAgreement',
            theme: 'general',
        },
    },
    {
        path: '/schedule',
        name: 'Schedule',
        component: () => import('@/components/calendar/GeneratedSchedule.vue'),
        meta: {
            permission_requirement: 'canSeeSchedule',
            theme: 'general',
        },
    },
    {
        path: '/password',
        name: 'Password',
        component: () => import('@/components/config/Password.vue'),
        meta: {
            permission_requirement: undefined,
            theme: 'general',
        },
    },
    {
        path: '/role',
        name: 'Role',
        component: () => import('@/components/role/Role.vue'),
        meta: {
            permission_requirement: 'canSeeRole',
            theme: 'general',
        },
    },
    {
        path: '/director/dashboard',
        name: 'Director Dashboard',
        component: () => import('@/components/director/DirectorDashboard.vue'),
        meta: {
            permission_requirement: 'canSeeDirDashboard',
            theme: 'general',
        },
    },
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'navbar-selected',
    linkExactActiveClass: 'navbar-selected',
})

router.beforeEach(async (to, from) => {
    const authUserStore = useAuthUserStore();
    const permissionStore = usePermissionStore();
    
    if (authUserStore.isLoggedIn) {
        authUserStore.updateCurrentRoute(to.name);
    }

    if (!authUserStore.isLoggedIn && !['Login', 'Diagnostic'].includes(to.name)) {
        useRememberStore().updatePage('routeBeforeLogin', 'route', to.fullPath);
        return { name: 'Login' }
    }

    if (to.name === 'Home' && permissionStore.canAccess('canMakeCalls')) {
        return { name: 'Client Chat' }
    }
    
    if (to.meta.permission_requirement !== undefined && !permissionStore.canAccess(to.meta.permission_requirement)) {
        return { name: '_403' }
    }

})

export default router